import { cn } from "@/lib/utils";
import { ChevronDown } from "lucide-react";
import type { CSSProperties } from "react";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/molecules/Accordion";
import { expansionClick } from "@/customAnalytics/trackingEvents";
import type { Faq3Row, Faq3Theme } from "./viewModel";

type Props = {
  theme: Faq3Theme;
  items: Faq3Row[];
  categoryName: string;
  expandedFaqId: string | null;
  onExpandedChange: (id: string | null) => void;
};

function trackToggle(
  items: Faq3Row[],
  categoryName: string,
  newId: string | null,
  previousId: string | null,
) {
  const row = (id: string) => items.find((f) => f.id.toString() === id);
  const idx = (id: string) => items.findIndex((f) => f.id.toString() === id);
  if (newId && newId !== previousId) {
    const f = row(newId);
    if (f) {
      expansionClick({
        section: "faqs_section",
        component: "faq_item",
        label: f.question,
        index: String(idx(newId)),
        entity_name: f.id.toString(),
        parent_entity: categoryName,
      });
    }
  } else if (!newId && previousId) {
    const f = row(previousId);
    if (f) {
      expansionClick({
        section: "faqs_section",
        component: "faq_item",
        label: f.question,
        index: String(idx(previousId)),
        entity_name: f.id.toString(),
        parent_entity: categoryName,
      });
    }
  }
}

export function Faq3NumberedAccordion({
  theme,
  items,
  categoryName,
  expandedFaqId,
  onExpandedChange,
}: Props) {
  const { brandColor, indexMuted, itemBorder, answerBg, answerBorder } = theme;

  return (
    <Accordion
      type="single"
      collapsible
      value={expandedFaqId || ""}
      onValueChange={(v) => {
        const next = v === "" ? null : v;
        trackToggle(items, categoryName, next, expandedFaqId);
        onExpandedChange(next);
      }}
      className="flex w-full flex-col gap-1.5"
    >
      {items.length > 0 ? (
        items.map((faq, index) => {
          const indexLabel = String(index + 1).padStart(2, "0");
          return (
            <AccordionItem
              key={faq.id}
              value={faq.id.toString()}
              className="border-0 p-0"
            >
              <div
                className="overflow-hidden rounded-md shadow-[0px_3px_3.4px_rgba(0,0,0,0.08)]"
                style={
                  {
                    ["--faq3-brand"]: brandColor,
                    ["--item-border"]: itemBorder,
                    ["--faq3-muted"]: indexMuted,
                    ["--faq3-q"]: faq.questionColor,
                  } as CSSProperties
                }
              >
                <AccordionTrigger
                  adminIcon={false}
                  className={cn(
                    "group w-full items-center gap-2 px-3 py-2.5 min-[640px]:gap-4 min-[640px]:px-8 min-[640px]:py-3.5",
                    "data-[state=open]:rounded-t-md data-[state=open]:border-0",
                    "data-[state=open]:!bg-[var(--faq3-brand)]",
                    "data-[state=closed]:rounded-md data-[state=closed]:border data-[state=closed]:border-[color:var(--item-border)] data-[state=closed]:bg-white",
                    "hover:no-underline",
                  )}
                >
                  <span
                    className={cn(
                      "w-9 shrink-0 text-xl font-bold tabular-nums tracking-wide min-[640px]:w-10 min-[640px]:text-2xl",
                      "text-[color:var(--faq3-muted)]",
                      "group-data-[state=open]:!text-white",
                    )}
                  >
                    {indexLabel}
                  </span>
                  <span
                    className={cn(
                      "min-w-0 flex-1 text-sm font-semibold leading-normal min-[640px]:text-sm",
                      "text-[color:var(--faq3-q)]",
                      "group-data-[state=open]:!text-white",
                    )}
                  >
                    {faq.question}
                  </span>
                  <ChevronDown
                    className={cn(
                      "mt-0.5 size-5 shrink-0 transition-transform duration-200 min-[640px]:size-6",
                      "text-[#171719] group-data-[state=open]:!rotate-180 group-data-[state=open]:!text-white",
                    )}
                    aria-hidden
                    strokeWidth={2.2}
                  />
                </AccordionTrigger>
                <AccordionContent className="!pt-0 !pb-0 data-[state=closed]:border-0">
                  <div
                    className="rounded-b-md border border-t-0 px-4 py-3.5 min-[640px]:px-4"
                    style={{
                      backgroundColor: answerBg,
                      borderColor: answerBorder,
                      boxShadow: "0px 3px 3.4px 0px rgba(0,0,0,0.08)",
                    }}
                  >
                    <p
                      className="text-sm leading-normal min-[640px]:text-sm"
                      style={{ color: faq.answerColor }}
                    >
                      {faq.answer}
                    </p>
                  </div>
                </AccordionContent>
              </div>
            </AccordionItem>
          );
        })
      ) : (
        <p className="py-6 text-center text-sm text-gray-500">
          No FAQs available for this category.
        </p>
      )}
    </Accordion>
  );
}
